import {
  ellipseFootprint,
  halfRoundOutline,
  rectFootprint,
  resolveFloorPlanShape,
  rotatePoints,
  serpentineOutline,
  spacingIssues,
  type ChairSides,
  type ShapePoint,
  type SpacingFootprint,
  type SpacingIssue,
} from '@peles/shared';
import {
  buildChairsForParent,
  canParentChairs,
  chairsOf,
  pxToFt,
  CHAIR_PARENT_FIELD,
  tableChairSides,
} from './helpers';
import type { CanvasItem } from './types';
import { isTableItem } from './types';

/**
 * Room between the things on a plan: which items take floor, what outline
 * each covers, and which pairs are too close. The geometry is `@peles/shared`
 * `floorSpacing`; this decides what a canvas item means to it.
 */

/**
 * Floor markings and the room's own fabric. A table standing in the cocktail
 * area or on the dance floor is where it is meant to be, and a door or a wall
 * is not something a guest walks around. Stairs take floor like any object:
 * a table on them, or in the way of them, is a real problem.
 */
const NO_FOOTPRINT = new Set([
  'AREA',
  'DANCE_FLOOR',
  'AISLE',
  'WALL_SEGMENT',
  'PARTITION',
  'WINDOW',
]);

export function takesFloor(item: CanvasItem): boolean {
  if (item.config?.isBackground) return false;
  if (item.objectType.startsWith('DOOR_')) return false;
  return !NO_FOOTPRINT.has(item.objectType);
}

/** The floor an item covers, in feet, rotated as it is drawn. */
export function itemFootprint(item: CanvasItem): ShapePoint[] {
  const x = pxToFt(item.x);
  const y = pxToFt(item.y);
  const w = item.widthFt;
  const h = item.heightFt;
  const cx = x + w / 2;
  const cy = y + h / 2;
  switch (resolveFloorPlanShape(item.objectType, item.config)) {
    case 'CIRCLE':
    case 'OVAL':
      return ellipseFootprint(x, y, w, h, item.rotation);
    case 'DIAMOND':
      return rotatePoints(
        [
          { x: cx, y },
          { x: x + w, y: cy },
          { x: cx, y: y + h },
          { x, y: cy },
        ],
        cx,
        cy,
        item.rotation,
      );
    case 'HEXAGON':
      return rotatePoints(
        [
          { x: x + w / 4, y },
          { x: x + (w * 3) / 4, y },
          { x: x + w, y: cy },
          { x: x + (w * 3) / 4, y: y + h },
          { x: x + w / 4, y: y + h },
          { x, y: cy },
        ],
        cx,
        cy,
        item.rotation,
      );
    case 'HALF_ROUND':
      return rotatePoints(
        halfRoundOutline(w, h).map((p) => ({ x: x + p.x, y: y + p.y })),
        cx,
        cy,
        item.rotation,
      );
    case 'SERPENTINE':
      return rotatePoints(
        serpentineOutline(w, h, item.config).map((p) => ({ x: x + p.x, y: y + p.y })),
        cx,
        cy,
        item.rotation,
      );
    default:
      return rectFootprint(x, y, w, h, item.rotation);
  }
}

export interface SpacingOptions {
  /** The chair size and sides the canvas previews unplaced chairs with. */
  chairSizeFt: number;
  chairSides: ChairSides;
}

/**
 * Every item's footprint, keyed by the uid of what a producer would move:
 * a placed chair belongs to its table, and a table with no chairs placed
 * yet still brings the ring they will sit in — a round of ten is a
 * nine-foot circle on the floor, not a six-foot one.
 */
export function planFootprints(items: CanvasItem[], opts: SpacingOptions): SpacingFootprint[] {
  const byUid = new Map(items.map((it) => [it.uid, it]));
  const out: SpacingFootprint[] = [];
  for (const item of items) {
    if (!takesFloor(item)) continue;
    const parentUid = item.objectType === 'CHAIR' ? item.config?.[CHAIR_PARENT_FIELD] : undefined;
    const owner =
      typeof parentUid === 'string' && byUid.has(parentUid) ? byUid.get(parentUid)! : item;
    const needsClearance = isTableItem(owner);
    out.push({ key: owner.uid, cluster: owner.uid, points: itemFootprint(item), needsClearance });
    if (
      isTableItem(item) &&
      canParentChairs(item) &&
      item.seats > 0 &&
      chairsOf(items, item).length === 0
    ) {
      // No chairs placed: the canvas previews the whole ring, and that is the
      // floor the table will take. Once any are placed, the placed chairs are
      // the answer — fewer than the seat count means seats removed by hand,
      // a gap left on purpose (a wheelchair space), not a chair to come.
      const ring = buildChairsForParent(item, {
        chairSizeFt: opts.chairSizeFt,
        chairSides: tableChairSides(item, opts.chairSides),
        sortBase: 0,
        groupId: '',
      });
      for (const chair of ring) {
        out.push({
          key: item.uid,
          cluster: item.uid,
          points: itemFootprint(chair),
          needsClearance,
        });
      }
    }
  }
  return out;
}

/**
 * The plan's spacing problems, one per pair of items — the worst of their
 * parts, so two tables whose chairs clash appear once, not once per chair.
 */
export function planSpacingIssues(
  items: CanvasItem[],
  clearanceFt: number,
  opts: SpacingOptions,
): SpacingIssue[] {
  return footprintIssues(planFootprints(items, opts), clearanceFt);
}

/** `planSpacingIssues` for footprints already worked out. */
export function footprintIssues(
  footprints: SpacingFootprint[],
  clearanceFt: number,
): SpacingIssue[] {
  const seen = new Set<string>();
  const out: SpacingIssue[] = [];
  for (const issue of spacingIssues(footprints, clearanceFt)) {
    const pair = [issue.a, issue.b].sort().join('|');
    if (seen.has(pair)) continue;
    seen.add(pair);
    out.push(issue);
  }
  return out;
}

/**
 * Pairs that overlap now and did not before a move. The move-lock asks this,
 * so a plan that already has an overlap can still be tidied up item by item
 * instead of every drag being refused for a problem it did not cause.
 */
export function newOverlaps(
  before: CanvasItem[],
  after: CanvasItem[],
  opts: SpacingOptions,
): SpacingIssue[] {
  const overlaps = (items: CanvasItem[]) =>
    planSpacingIssues(items, 0, opts).filter((i) => i.kind === 'overlap');
  const had = new Set(overlaps(before).map((i) => [i.a, i.b].sort().join('|')));
  return overlaps(after).filter((i) => !had.has([i.a, i.b].sort().join('|')));
}

/** "Table 4" for a table, its label or type otherwise. */
export function spacingItemName(item: CanvasItem | undefined): string {
  if (!item) return 'An item';
  if (isTableItem(item)) return `Table ${item.tableNumber}`;
  if (item.label) return item.label;
  return item.objectType
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
